import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Form, Modal, Spinner } from 'react-bootstrap';
import nursingTestCatalogService from '../../../services/nursingTestCatalogService';
import Icon from '../../common/Icons';
import { useCatalogToast } from '../../../hooks/useCatalogToast';
import { HEAD_DOCTOR_COPY } from '../headDoctorCopy';
import { HeadDoctorPageHeader } from '../ui';
import '../HeadDoctorDashboard.css';

const EMPTY_FORM = {
  name: '',
  is_active: true,
  sort_order: 0
};

function buildItemPayload(item, overrides = {}) {
  return {
    name: item.name,
    sort_order: item.sort_order ?? 0,
    is_active: item.is_active,
    ...overrides
  };
}

export default function NursingTestCatalogManager({ copy: copyProp }) {
  const copy = copyProp ?? HEAD_DOCTOR_COPY.NURSING_TEST_CATALOG;
  const toast = useCatalogToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [modalError, setModalError] = useState('');
  const [items, setItems] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingItem, setEditingItem] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (!showInactive) params.is_active = 'true';

      const res = await nursingTestCatalogService.list(params);
      if (res.success) {
        setItems(Array.isArray(res.data) ? res.data : []);
      } else {
        setError(res.message || 'Chargement impossible');
      }
    } catch (e) {
      setError(e.message || 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  }, [search, showInactive]);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingItem(null);
  };

  const openCreate = () => {
    resetForm();
    setModalError('');
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm({
      name: item.name || '',
      is_active: Boolean(item.is_active),
      sort_order: item.sort_order ?? 0
    });
    setModalError('');
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setModalError('');
    resetForm();
  };

  const buildSubmitPayload = () => ({
    name: form.name.trim(),
    sort_order: editingItem ? form.sort_order : 0,
    is_active: form.is_active
  });

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setModalError('');
    try {
      const payload = buildSubmitPayload();
      if (!payload.name) throw new Error('Le libellé est obligatoire.');

      if (editingItem) {
        await nursingTestCatalogService.update(editingItem.id, payload);
        toast('success', 'Test mis à jour.');
      } else {
        await nursingTestCatalogService.create(payload);
        toast('success', 'Test ajouté.');
      }
      closeModal();
      await loadItems();
    } catch (err) {
      setModalError(err.message || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (item) => {
    if (!window.confirm(`Désactiver le test « ${item.name} » ?`)) return;
    try {
      await nursingTestCatalogService.update(item.id, buildItemPayload(item, { is_active: false }));
      toast('success', 'Test désactivé.');
      if (editingItem?.id === item.id) closeModal();
      await loadItems();
    } catch (err) {
      setError(err.message || 'Désactivation impossible');
    }
  };

  const isEditing = Boolean(editingItem);

  return (
    <div className="doctor-dashboard recv-dash-pro--main hd-nursing-test-page">
      <HeadDoctorPageHeader
        badge={copy.BADGE}
        title={copy.PAGE_TITLE}
        description={copy.PAGE_SUBTITLE}
        actions={
          <button type="button" className="hd-co-create-btn" onClick={openCreate}>
            <Icon name="plus" size={16} aria-hidden />
            {copy.NEW_BUTTON}
          </button>
        }
      />

      <div className="hd-co-shell">
        <div className="hd-co-toolbar">
          <div className="hd-co-search-wrap">
            <Icon name="search" size={16} className="hd-co-search-icon" aria-hidden />
            <input
              type="search"
              className="hd-co-search-input"
              placeholder={copy.SEARCH_PLACEHOLDER}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label={copy.SEARCH_PLACEHOLDER}
            />
          </div>
          <div className="hd-co-toolbar-end">
            <label className="hd-exam-inactive-toggle mb-0">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              <span>{copy.FILTER_INACTIVE}</span>
            </label>
            <button
              type="button"
              className="hd-co-refresh-btn"
              onClick={loadItems}
              disabled={loading}
              aria-label={copy.REFRESH_ARIA}
              title={copy.REFRESH_ARIA}
            >
              <Icon name="refresh" size={18} aria-hidden />
            </button>
          </div>
        </div>

        {error ? <Alert variant="danger" className="hd-co-alert mb-0">{error}</Alert> : null}

        {loading ? (
          <div className="hd-co-state" role="status" aria-live="polite">
            <Spinner animation="border" className="hd-co-state__spinner" />
            <p className="hd-co-state__text mb-0">{copy.LOADING}</p>
          </div>
        ) : items.length === 0 ? (
          <div className="hd-co-state hd-co-state--empty">
            <div className="hd-co-state__icon" aria-hidden>
              <Icon name="check" size={32} />
            </div>
            <h3 className="hd-co-state__title">{copy.EMPTY_TITLE}</h3>
            <p className="hd-co-state__text">{copy.EMPTY}</p>
            <button type="button" className="hd-co-create-btn" onClick={openCreate}>
              <Icon name="plus" size={16} aria-hidden />
              {copy.NEW_BUTTON}
            </button>
          </div>
        ) : (
          <div className="hd-co-table-wrap table-responsive">
            <table className="table table-hover align-middle hd-dis-table hd-nursing-care-table mb-0">
              <thead>
                <tr>
                  <th>{copy.COL_LABEL}</th>
                  <th>{copy.COL_STATUS}</th>
                  <th className="text-end hd-nursing-care-table__col-actions">{copy.COL_ACTIONS}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item.id}
                    className={
                      showModal && editingItem?.id === item.id ? 'hd-dis-row--editing' : undefined
                    }
                  >
                    <td>
                      <span className={`hd-dis-name${!item.is_active ? ' text-muted' : ''}`}>
                        {item.name}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`hd-exam-status-pill ${
                          item.is_active
                            ? 'hd-exam-status-pill--active'
                            : 'hd-exam-status-pill--inactive'
                        }`}
                      >
                        <span className="hd-exam-status-pill__dot" aria-hidden />
                        {item.is_active ? copy.STATUS_ACTIVE : copy.STATUS_INACTIVE}
                      </span>
                    </td>
                    <td className="text-end">
                      <div className="hd-nursing-care-table__actions">
                        <button
                          type="button"
                          className="hd-nursing-care-table__btn"
                          onClick={() => openEdit(item)}
                        >
                          <Icon name="edit" size={14} aria-hidden />
                          <span>{copy.ACTION_EDIT}</span>
                        </button>
                        {item.is_active ? (
                          <button
                            type="button"
                            className="hd-nursing-care-table__btn hd-nursing-care-table__btn--danger"
                            onClick={() => deactivate(item)}
                          >
                            <span>{copy.ACTION_DEACTIVATE}</span>
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        show={showModal}
        onHide={closeModal}
        centered
        className="hd-dis-modal-root"
        backdropClassName="hd-dis-modal-backdrop"
        dialogClassName="hd-dis-modal-dialog"
        contentClassName="hd-dis-modal__content"
        backdrop={saving ? 'static' : true}
        keyboard={!saving}
      >
        <div className="hd-dis-modal__header">
          <div className="hd-dis-modal__header-main">
            <span className="hd-dis-modal__icon" aria-hidden>
              <Icon name="check" size={22} />
            </span>
            <div className="hd-dis-modal__titles">
              <span
                className={`hd-dis-modal__mode${
                  isEditing ? ' hd-dis-modal__mode--edit' : ' hd-dis-modal__mode--new'
                }`}
              >
                {isEditing ? copy.MODAL_MODE_EDIT : copy.MODAL_MODE_NEW}
              </span>
              <h2 className="hd-dis-modal__title">
                {isEditing ? copy.MODAL_TITLE_EDIT : copy.MODAL_TITLE_NEW}
              </h2>
            </div>
          </div>
          <button
            type="button"
            className="hd-dis-modal__close"
            onClick={closeModal}
            aria-label={copy.MODAL_CLOSE_ARIA}
          >
            <Icon name="times" size={18} aria-hidden />
          </button>
        </div>

        <Form onSubmit={submit}>
          <div className="hd-dis-modal__body">
            {modalError ? (
              <Alert variant="danger" className="hd-dis-modal__alert mb-0">
                {modalError}
              </Alert>
            ) : null}

            <div className="hd-dis-modal__fields">
              <div className="hd-dis-modal__field">
                <label className="hd-dis-modal__label" htmlFor="hd-nursing-test-name">
                  {copy.FIELD_LABEL} *
                </label>
                <input
                  id="hd-nursing-test-name"
                  type="text"
                  className="hd-dis-modal__input"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder={copy.FIELD_LABEL_PLACEHOLDER}
                  maxLength={255}
                  required
                  autoFocus
                  autoComplete="off"
                />
              </div>

              <div className="hd-dis-modal__field hd-dis-modal__field--full">
                <label className="hd-exam-inactive-toggle hd-partner-active-toggle">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, is_active: e.target.checked }))
                    }
                  />
                  <span>{copy.FIELD_ACTIVE}</span>
                </label>
              </div>
            </div>
          </div>

          <div className="hd-dis-modal__footer">
            <button
              type="button"
              className="hd-dis-modal__btn-cancel"
              onClick={closeModal}
              disabled={saving}
            >
              {copy.BTN_CANCEL}
            </button>
            <button type="submit" className="hd-co-create-btn" disabled={saving}>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" aria-hidden />
                  {copy.BTN_SAVE}
                </>
              ) : isEditing ? (
                copy.BTN_UPDATE
              ) : (
                <>
                  <Icon name="plus" size={16} aria-hidden />
                  {copy.BTN_CREATE}
                </>
              )}
            </button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
